export interface NotificationMessageSource {
  type?: string | null;
  metadata?: unknown;
  title?: string | null;
  body?: string | null;
}

type Message = { title: string; body: string };
type Text = readonly [string, string];
type Definition = { family: string; title: Text; body: Text };

// Shared resources keep every dashboard and the customer inbox on the same copy.
const definitions: Record<string, Definition> = {};
function add(types: string[], family: string, title: Text, body: Text) {
  for (const type of types) definitions[type] = { family, title, body };
}

add(
  ['documents.customer-order-invoice-issued'],
  'invoice',
  ['Order invoice issued', 'صدرت فاتورة الطلب'],
  [
    'Invoice {invoiceNumber} for order {orderNumber}: {grandTotal} {currency}.',
    'فاتورة {invoiceNumber} للطلب {orderNumber}: {grandTotal} {currency}.',
  ],
);
add(
  ['ordering.customer-seller-order-shipped', 'ordering.seller-seller-order-shipped'],
  'order',
  ['Seller order shipped', 'تم شحن طلب البائع'],
  [
    'Seller order {sellerOrderNumber} is on its way.',
    'طلب البائع {sellerOrderNumber} في الطريق.',
  ],
);
add(
  ['ordering.customer-seller-order-delivered'],
  'order',
  ['Your seller order was delivered', 'تم توصيل طلبك من البائع'],
  [
    'Seller order {sellerOrderNumber} was delivered. Open the order to confirm receipt or report a receipt problem.',
    'تم توصيل طلب البائع {sellerOrderNumber}. افتح الطلب لتأكيد الاستلام أو الإبلاغ عن مشكلة في الاستلام.',
  ],
);
add(
  ['ordering.seller-seller-order-delivered'],
  'order',
  ['Seller order delivered', 'تم توصيل طلب البائع'],
  [
    'Seller order {sellerOrderNumber} was marked as delivered.',
    'تم تحديد طلب البائع {sellerOrderNumber} كطلب تم توصيله.',
  ],
);
add(
  [
    'ordering.seller-receipt-complaint-opened',
    'ordering.admin-receipt-complaint-opened',
  ],
  'order',
  ['Receipt complaint opened', 'تم فتح شكوى استلام'],
  [
    'A receipt complaint was opened for seller order {sellerOrderNumber}. Open the order for details.',
    'تم فتح شكوى استلام لطلب البائع {sellerOrderNumber}. افتح الطلب للاطلاع على التفاصيل.',
  ],
);
add(
  ['ordering.customer-seller-order-fully-accepted'],
  'decision',
  ['Seller order accepted', 'تم قبول طلب البائع'],
  [
    'Seller order {sellerOrderNumber} was fully accepted. {itemCount} item(s) accepted. Updated total: {orderTotal} {orderTotalCurrency}.',
    'تم قبول طلب البائع {sellerOrderNumber} بالكامل. تم قبول {itemCount} من المنتجات. الإجمالي المحدّث: {orderTotal} {orderTotalCurrency}.',
  ],
);
add(
  ['ordering.customer-seller-order-partially-accepted'],
  'decision',
  ['Some seller order items were accepted', 'تم قبول بعض منتجات طلب البائع'],
  [
    'The seller accepted {itemCount} item(s) from seller order {sellerOrderNumber}. Updated total: {orderTotal} {orderTotalCurrency}.',
    'قبل البائع {itemCount} من منتجات طلب البائع {sellerOrderNumber}. الإجمالي المحدّث: {orderTotal} {orderTotalCurrency}.',
  ],
);
add(
  ['ordering.customer-seller-order-fully-rejected'],
  'decision',
  ['Seller order rejected', 'تم رفض طلب البائع'],
  [
    'Seller order {sellerOrderNumber} was rejected ({itemCount} item(s)). Updated total: {orderTotal} {orderTotalCurrency}.',
    'تم رفض طلب البائع {sellerOrderNumber} ({itemCount} من المنتجات). الإجمالي المحدّث: {orderTotal} {orderTotalCurrency}.',
  ],
);
add(
  ['ordering.customer-seller-order-partially-rejected'],
  'decision',
  ['Some seller order items were rejected', 'تم رفض بعض منتجات طلب البائع'],
  [
    'The seller rejected {itemCount} item(s) from seller order {sellerOrderNumber}. Updated total: {orderTotal} {orderTotalCurrency}.',
    'رفض البائع {itemCount} من منتجات طلب البائع {sellerOrderNumber}. الإجمالي المحدّث: {orderTotal} {orderTotalCurrency}.',
  ],
);
add(
  ['ordering.seller-seller-order-cancelled-by-customer'],
  'order',
  ['Seller order cancelled by customer', 'ألغى العميل طلب البائع'],
  [
    'The customer cancelled seller order {sellerOrderNumber}.',
    'ألغى العميل طلب البائع {sellerOrderNumber}.',
  ],
);
add(
  ['ordering.seller-seller-order-receipt-confirmed-by-customer'],
  'order',
  ['Customer confirmed receipt', 'أكد العميل الاستلام'],
  [
    'The customer confirmed receipt for seller order {sellerOrderNumber}.',
    'أكد العميل استلام طلب البائع {sellerOrderNumber}.',
  ],
);
add(
  [
    'ordering.seller-seller-order-receipt-auto-confirmed',
    'ordering.customer-seller-order-receipt-auto-confirmed',
  ],
  'order',
  [
    'Seller order receipt automatically finalized',
    'تم تأكيد استلام طلب البائع تلقائياً',
  ],
  [
    'Receipt for seller order {sellerOrderNumber} was automatically finalized after the confirmation period.',
    'تم تأكيد استلام طلب البائع {sellerOrderNumber} تلقائياً بعد انتهاء فترة التأكيد.',
  ],
);
add(
  ['inventory.seller-low-stock'],
  'inventory',
  ['Low stock alert', 'تنبيه انخفاض المخزون'],
  [
    'Variant {variantSku} has {availableQuantity} available item(s), at or below its reorder level of {reorderLevel}.',
    'الكمية المتاحة للمتغير {variantSku} هي {availableQuantity}، عند حد إعادة الطلب {reorderLevel} أو أقل منه.',
  ],
);
add(
  ['inventory.seller-out-of-stock'],
  'inventory',
  ['Out of stock alert', 'تنبيه نفاد المخزون'],
  [
    'Variant {variantSku} is out of stock. Replenish its inventory to resume selling.',
    'نفد مخزون المتغير {variantSku}. أعد تزويد المخزون لاستئناف البيع.',
  ],
);
add(
  ['catalog.admin-product-draft-submitted'],
  'catalog',
  ['Product draft requires review', 'مسودة منتج تحتاج إلى مراجعة'],
  [
    'A product draft was submitted for moderation. Open the draft queue.',
    'تم إرسال مسودة منتج للمراجعة. افتح قائمة المسودات.',
  ],
);
add(
  ['catalog.seller-product-draft-approved'],
  'catalog',
  ['Product draft approved', 'تمت الموافقة على مسودة المنتج'],
  [
    'Your product draft was approved. Open the draft for its current status.',
    'تمت الموافقة على مسودة منتجك. افتح المسودة للاطلاع على حالتها الحالية.',
  ],
);
add(
  ['catalog.seller-product-draft-rejected'],
  'catalog',
  ['Product draft needs attention', 'مسودة المنتج تحتاج إلى انتباه'],
  [
    'Your product draft was not approved. {sellerFeedback}',
    'لم تتم الموافقة على مسودة منتجك. {sellerFeedback}',
  ],
);
for (const entity of ['product', 'store']) {
  const label: Text =
    entity === 'product' ? ['Product', 'المنتج'] : ['Store', 'المتجر'];
  add(
    [`reviews.admin-${entity}-review-submitted`],
    'review',
    [`${label[0]} review requires moderation`, `تقييم ${label[1]} يحتاج إلى مراجعة`],
    [
      'A review was submitted for moderation. Open the review queue.',
      'تم إرسال تقييم للمراجعة. افتح قائمة التقييمات.',
    ],
  );
  add(
    [`reviews.customer-${entity}-review-approved`],
    'review',
    [`${label[0]} review approved`, `تمت الموافقة على تقييم ${label[1]}`],
    [
      'Your review was approved and is now visible.',
      'تمت الموافقة على تقييمك وأصبح ظاهراً الآن.',
    ],
  );
  add(
    [`reviews.seller-${entity}-review-approved`],
    'review',
    [`${label[0]} review approved`, `تمت الموافقة على تقييم ${label[1]}`],
    [
      'A review for your store was approved and is now visible.',
      'تمت الموافقة على تقييم لمتجرك وأصبح ظاهراً الآن.',
    ],
  );
  add(
    [`reviews.customer-${entity}-review-rejected`],
    'review',
    [`${label[0]} review needs attention`, `تقييم ${label[1]} يحتاج إلى انتباه`],
    [
      'Your review was not approved. {customerFacingReason}',
      'لم تتم الموافقة على تقييمك. {customerFacingReason}',
    ],
  );
}
for (const role of ['seller', 'driver']) {
  const label: Text = role === 'seller' ? ['Seller', 'البائع'] : ['Driver', 'السائق'];
  add(
    [`iam.customer-${role}-application-approved`],
    'empty',
    [`${label[0]} application approved`, `تمت الموافقة على طلب ${label[1]}`],
    [
      'Your application was approved. Open your application for details.',
      'تمت الموافقة على طلبك. افتح الطلب للاطلاع على التفاصيل.',
    ],
  );
  add(
    [`iam.customer-${role}-application-rejected`],
    'empty',
    [`${label[0]} application needs attention`, `طلب ${label[1]} يحتاج إلى انتباه`],
    [
      'Your application was not approved. Open your application for details.',
      'لم تتم الموافقة على طلبك. افتح الطلب للاطلاع على التفاصيل.',
    ],
  );
}
add(
  ['customer-support.assignee-ticket-assigned'],
  'empty',
  ['Support ticket assigned', 'تم تعيين تذكرة دعم'],
  [
    'A support ticket was assigned to you. Open the ticket for details.',
    'تم تعيين تذكرة دعم لك. افتح التذكرة للاطلاع على التفاصيل.',
  ],
);
add(
  ['customer-support.admin-unassigned-ticket-created'],
  'empty',
  ['Unassigned support ticket', 'تذكرة دعم غير معيّنة'],
  [
    'A support ticket needs assignment. Open the support queue.',
    'تحتاج تذكرة دعم إلى تعيين. افتح قائمة الدعم.',
  ],
);
add(
  [
    'customer-support.customer-ticket-comment-added',
    'customer-support.assignee-ticket-comment-added',
    'customer-support.admin-ticket-comment-added',
  ],
  'empty',
  ['New support ticket comment', 'تعليق جديد على تذكرة الدعم'],
  [
    'A participant added a comment to a support ticket. Open the ticket for details.',
    'أضاف أحد المشاركين تعليقاً على تذكرة دعم. افتح التذكرة للاطلاع على التفاصيل.',
  ],
);
add(
  ['customer-support.customer-ticket-resolved'],
  'empty',
  ['Support ticket resolved', 'تم حل تذكرة الدعم'],
  [
    'Your support ticket was resolved. Open the ticket for details.',
    'تم حل تذكرة الدعم الخاصة بك. افتح التذكرة للاطلاع على التفاصيل.',
  ],
);
const delivery: Array<[string, Text, Text]> = [
  [
    'driver-route-offer',
    ['New delivery offer', 'عرض توصيل جديد'],
    [
      'Route {routeNumber} is available for your response.',
      'المسار {routeNumber} متاح لردك.',
    ],
  ],
  [
    'driver-route-offer-expiring-soon',
    ['Delivery offer expiring soon', 'عرض التوصيل سينتهي قريباً'],
    [
      'Route offer {routeNumber} will expire soon.',
      'سينتهي عرض المسار {routeNumber} قريباً.',
    ],
  ],
  [
    'driver-route-offer-timed-out',
    ['Delivery offer expired', 'انتهت صلاحية عرض التوصيل'],
    ['Route offer {routeNumber} expired.', 'انتهت صلاحية عرض المسار {routeNumber}.'],
  ],
  [
    'driver-route-offer-withdrawn',
    ['Delivery offer withdrawn', 'تم سحب عرض التوصيل'],
    ['Route offer {routeNumber} was withdrawn.', 'تم سحب عرض المسار {routeNumber}.'],
  ],
  [
    'driver-route-request-approved',
    ['Assignment request approved', 'تمت الموافقة على طلب التعيين'],
    [
      'Your request for route {routeNumber} was approved.',
      'تمت الموافقة على طلبك للمسار {routeNumber}.',
    ],
  ],
  [
    'driver-route-request-rejected',
    ['Assignment request rejected', 'تم رفض طلب التعيين'],
    [
      'Your request for route {routeNumber} was rejected.',
      'تم رفض طلبك للمسار {routeNumber}.',
    ],
  ],
  [
    'driver-route-request-timed-out',
    ['Assignment request timed out', 'انتهت مهلة طلب التعيين'],
    [
      'Your request for route {routeNumber} timed out.',
      'انتهت مهلة طلبك للمسار {routeNumber}.',
    ],
  ],
  [
    'driver-order-claim-timed-out',
    ['Order claim timed out', 'انتهت مهلة طلب استلام الطلب'],
    [
      'Your delivery claim {routeNumber} timed out.',
      'انتهت مهلة طلب التوصيل {routeNumber}.',
    ],
  ],
  [
    'driver-recovery-retry-approved',
    ['Delivery retry approved', 'تمت الموافقة على إعادة محاولة التوصيل'],
    [
      'A delivery retry was approved for route {routeNumber}.',
      'تمت الموافقة على إعادة محاولة التوصيل للمسار {routeNumber}.',
    ],
  ],
  [
    'driver-recovery-return-required',
    ['Return required', 'الإرجاع مطلوب'],
    [
      'A package from route {routeNumber} must be returned.',
      'يجب إرجاع طرد من المسار {routeNumber}.',
    ],
  ],
  [
    'driver-admin-route-action-required',
    ['Delivery route needs attention', 'مسار التوصيل يحتاج إلى انتباه'],
    [
      'Route {routeNumber} requires review. Open the route for details.',
      'يحتاج المسار {routeNumber} إلى مراجعة. افتح المسار للاطلاع على التفاصيل.',
    ],
  ],
];
for (const [type, title, body] of delivery)
  add([`delivery.${type}`], 'delivery', title, body);
add(
  ['refunds.admin-request-submitted'],
  'refund',
  ['Refund request submitted', 'تم تقديم طلب استرداد'],
  [
    'Refund request {requestCode} requires review.',
    'طلب الاسترداد {requestCode} يحتاج إلى مراجعة.',
  ],
);
add(
  ['refunds.customer-decision-recorded', 'refunds.seller-decision-recorded'],
  'refund',
  ['Refund decision recorded', 'تم تسجيل قرار الاسترداد'],
  [
    'A decision was recorded for refund request {requestCode}. Open the request for details.',
    'تم تسجيل قرار لطلب الاسترداد {requestCode}. افتح الطلب للاطلاع على التفاصيل.',
  ],
);

export const NOTIFICATION_MESSAGE_TYPES = Object.keys(definitions);

function validMetadata(
  family: string,
  value: unknown,
): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const m = value as Record<string, unknown>;
  const text = (key: string) =>
    typeof m[key] === 'string' &&
    (m[key] as string).trim().length > 0 &&
    (m[key] as string).length <= 2000;
  const optionalText = (key: string) => m[key] === null || text(key);
  const number = (key: string) =>
    typeof m[key] === 'number' && Number.isFinite(m[key]) && (m[key] as number) >= 0;
  switch (family) {
    case 'empty':
      return true;
    case 'order':
      return optionalText('sellerOrderNumber');
    case 'decision':
      return (
        text('sellerOrderNumber') &&
        number('itemCount') &&
        Number.isInteger(m['itemCount']) &&
        number('orderTotal') &&
        text('orderTotalCurrency')
      );
    case 'inventory':
      return (
        text('variantSku') &&
        number('availableQuantity') &&
        Number.isInteger(m['availableQuantity']) &&
        number('reorderLevel') &&
        Number.isInteger(m['reorderLevel'])
      );
    case 'catalog':
      return optionalText('sellerFeedback');
    case 'review':
      return optionalText('customerFacingReason');
    case 'refund':
      return (
        text('requestCode') &&
        (m['approved'] === null || typeof m['approved'] === 'boolean')
      );
    case 'delivery':
      return optionalText('routeNumber') && text('eventType');
    case 'invoice':
      return (
        text('orderNumber') &&
        text('invoiceNumber') &&
        number('generationCount') &&
        Number.isInteger(m['generationCount']) &&
        text('issuedAtUtc') &&
        Number.isFinite(Date.parse(m['issuedAtUtc'] as string)) &&
        number('grandTotal') &&
        text('currency') &&
        typeof m['isPrepaid'] === 'boolean'
      );
    default:
      return false;
  }
}

export function renderNotificationMessage(
  item: NotificationMessageSource,
  language: string,
): Message {
  const index = language.toLowerCase().startsWith('ar') ? 1 : 0;
  const definition =
    item.type && Object.hasOwn(definitions, item.type)
      ? definitions[item.type]
      : null;
  if (definition && validMetadata(definition.family, item.metadata)) {
    const metadata = item.metadata;
    let body = definition.body[index];
    if (
      definition.family === 'refund' &&
      item.type !== 'refunds.admin-request-submitted'
    ) {
      if (metadata['approved'] === true) {
        body = index
          ? 'تمت الموافقة على طلب الاسترداد {requestCode}. افتح الطلب للاطلاع على التفاصيل.'
          : 'Refund request {requestCode} was approved. Open the request for details.';
      } else if (metadata['approved'] === false) {
        body = index
          ? 'تم رفض طلب الاسترداد {requestCode}. افتح الطلب للاطلاع على التفاصيل.'
          : 'Refund request {requestCode} was rejected. Open the request for details.';
      }
    }
    for (const key of ['sellerOrderNumber', 'routeNumber']) {
      if (metadata[key] === null) body = body.replace(` {${key}}`, '');
    }
    const interpolate = (template: string) =>
      template.replace(/\{([a-zA-Z]+)\}/g, (_, key: string) => {
        const value = metadata[key];
        return typeof value === 'number'
          ? new Intl.NumberFormat(index ? 'ar' : 'en', {
              maximumFractionDigits: 5,
            }).format(value)
          : typeof value === 'string'
            ? value
            : '';
      });
    return {
      title: interpolate(definition.title[index]),
      body: interpolate(body),
    };
  }
  // Legacy snapshots remain plain text; never interpret server copy as HTML or keys.
  return {
    title:
      typeof item.title === 'string' && item.title.trim()
        ? item.title
        : index
          ? 'إشعار'
          : 'Notification',
    body:
      typeof item.body === 'string' && item.body.trim()
        ? item.body
        : index
          ? 'لديك إشعار جديد.'
          : 'You have a new notification.',
  };
}
